<template>
  <div v-if="canRender" class="level-item action" @click="renderActiveView">
    <el-tooltip :content="$t('components.mesh-editor.tooltip.renderView')" placement="bottom">
      <font-awesome-icon icon="play" />
    </el-tooltip>
  </div>
  <div v-if="canRender" class="level-item action" :class="{ disabled: itemCameraCount === 0 }" @click="(e: any) => multipleCameraCapture(e)">
    <el-tooltip :content="$t('components.mesh-editor.tooltip.renderAll')" placement="bottom">
      <font-awesome-icon icon="forward" />
    </el-tooltip>
  </div>
  <el-popover
    v-if="canRender"
    ref="popoverAddRenderView"
    placement="bottom-start"
    :title="$t('components.mesh-editor.render.title')"
    :width="200"
    trigger="click"
  >
    <template #reference>
      <div class="level-item action">
        <el-tooltip :content="$t('components.mesh-editor.tooltip.addCamera')" placement="bottom">
          <font-awesome-icon icon="circle-plus" />
        </el-tooltip>
      </div>
    </template>
    <el-form ref="dataForm" :model="cameraData" @submit="addCamera" v-loading.fullscreen="isLoadingCapture">
      <el-form-item :label="$t('components.mesh-editor.render.view')" prop="view">
        <el-radio-group v-model="cameraData.view" @change="changeView">
          <el-radio v-for="item in Object.values(Views)" :key="item" :label="item">
            <font-awesome-icon :icon="['fac', item]" />
            {{ $t(`enum.views.${item}`) }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        :label="$t('components.mesh-editor.render.name')"
        prop="name"
        :rules="{
          required: true,
        }"
      >
        <el-input v-model="cameraData.view" />
      </el-form-item>
      <!--<el-form-item :label="$t('components.mesh-editor.render.perspective')">
        <el-checkbox v-model="cameraData.perspective" />
      </el-form-item>-->
      <el-form-item>
        <el-button type="primary" :disabled="cameraData.name.length === 0" @click="addCamera">
          {{ $t('components.mesh-editor.render.add') }}
        </el-button>
        <el-button type="primary" :disabled="itemCameraCount === 0" @click="multipleCameraCapture">
          {{ $t('components.mesh-editor.render.exportPdf') }}
        </el-button>
      </el-form-item>
    </el-form>
  </el-popover>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import { RendererPublicInterface } from 'troisjs';
import * as THREE from 'three';
import { ElForm } from 'element-plus';

import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import { SelectionList } from '@/types/ui/SelectionList';
import { ViewCtrl } from '@/types/ui/ViewCtrl';
import { EditorMode } from '@/types/ui/EditorMode';
import TransformTools from '@/components/three/tools/TransformTools.vue';
import * as THREEEnum from '@/types/enum/three';
import { SpecialObjectPrefix } from '@/types/enum/three';
import { HistoryInfo, HistoryList, HistoryOperationType } from '@/types/ui/HistoryList';
import { CameraExportData } from '@/services/api/modelService';
import { createPDFRenderObjectView, generatePDF } from '@/utils/pdfTemplates';
import type Node from 'element-plus/es/components/tree/src/model/node';
import { BlobURL } from '@/types/interfaces/blobUrl';

interface CameraData {
  cameraId: number | null;
  name: string;
  view: THREEEnum.Views;
  perspective: boolean;
}

interface CameraDescription {
  info: CameraData;
  uuid: string;
  position: THREE.Vector3;
  rotation: THREE.Euler;
  camera: THREE.Camera;
}

@Component({
  components: { TransformTools },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class CurveTools extends Vue implements HistoryInfo {
  //#region properties
  @Prop() modelValue!: MeshTreeData;
  @Prop() troisRenderer!: RendererPublicInterface;
  @Prop() editorMode!: EditorMode;
  @Prop() selectionList!: SelectionList;
  @Prop() viewCtrl!: ViewCtrl;
  @Prop({ default: true }) canRender!: boolean;
  @Prop() readonly selectObject!: (target: THREE.Object3D | null, setSelectionFlag: boolean) => Promise<void>;
  @Prop() historyList!: HistoryList;

  //history
  lastUpdateTime = -1;

  //curve
  cameraList: CameraDescription[] = [];
  cameraData: CameraData = {
    cameraId: null,
    name: THREEEnum.Views.custom,
    view: THREEEnum.Views.custom,
    perspective: true,
  };
  activeCamera: CameraDescription | null = null;

  // enums for use in template section
  Views = THREEEnum.Views;
  cameraViews: THREEEnum.Views[] = [];
  isLoadingCapture = false;
  itemCameraCount = 0;

  //#endregion properties

  //#region init
  mounted(): void {
    this.troisRenderer.three.cameraCtrl?.addEventListener('end', this.cameraChanged);
    this.troisRenderer.three.cameraCtrl?.addEventListener('start', this.cameraChangStart);
  }

  unmounted(): void {
    this.troisRenderer.three.cameraCtrl?.removeEventListener('end', this.cameraChanged);
    this.troisRenderer.three.cameraCtrl?.removeEventListener('start', this.cameraChangStart);
  }

  private oldPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private oldRotation: THREE.Euler = new THREE.Euler(0, 0, 0);
  cameraChangStart(): void {
    if (this.canRender && this.activeCamera) {
      this.oldPosition = this.activeCamera.position.clone();
      this.oldRotation = this.activeCamera.rotation.clone();
    }
  }

  cameraChanged(): void {
    if (this.canRender && this.activeCamera) {
      this.lastUpdateTime = Date.now();
      const cameraId = this.activeCamera.uuid;
      const newPosition = this.activeCamera.position.clone();
      const newRotation = this.activeCamera.rotation.clone();
      const oldPosition = this.oldPosition.clone();
      const oldRotation = this.oldRotation.clone();
      if (cameraId) {
        this.historyList.add(
          cameraId,
          HistoryOperationType.renderViews,
          async () => {
            this.undoRedoCamera(cameraId, oldPosition, oldRotation);
          },
          async () => {
            this.undoRedoCamera(cameraId, newPosition, newRotation);
          }
        );
      }
    }
  }

  @Watch('canRender', { immediate: true })
  onCanRenderChanged(): void {
    const cameraItem = this.modelValue.getItemByName((this as any).$t('enum.upload-category.camera'));
    if (cameraItem) {
      cameraItem.active = this.canRender;
      this.modelValue.updateStructure();
    }
  }
  //#endregion init

  //#region add / select camera
  addCamera(event: Event | null = null): void {
    if (event) event.preventDefault();

    const dataForm = this.$refs.dataForm as typeof ElForm;

    dataForm?.validate(async (valid: any) => {
      if (valid) {
        this.saveCamera(
          this.cameraData.cameraId,
          this.cameraData.name,
          this.cameraData.perspective,
          this.cameraData.view,
          this.troisRenderer.camera?.position,
          this.troisRenderer.camera?.rotation,
          true
        );
        this.modelValue.updateStructure();
        if (this.cameraData.view !== THREEEnum.Views.custom) {
          setTimeout(() => {
            this.viewCtrl.changeView(this.cameraData.view);
          }, 100);
        }
      }
    });
  }

  private saveCamera(
    cameraId: number | null,
    cameraName: string,
    perspective: boolean,
    view: THREEEnum.Views,
    position: THREE.Vector3 | undefined,
    rotation: THREE.Euler | undefined,
    selectNewCamera: boolean
  ): void {
    let camera: THREE.Camera;
    if (perspective) {
      camera = new THREE.PerspectiveCamera();
    } else {
      camera = new THREE.OrthographicCamera();
    }
    camera.name = cameraName;
    if (position && rotation) {
      camera.position.copy(position);
      camera.rotation.copy(rotation);
    }
    (camera as any).apiData = this.modelValue.addCamera(
      `${SpecialObjectPrefix.camera}${cameraName}`,
      camera,
      camera.position,
      camera.rotation,
      (this as any).$t('enum.upload-category.camera'),
      selectNewCamera
    );
    if ((camera as any).apiData) camera.uuid = (camera as any).apiData.uuid;
    const renderData: CameraDescription = {
      info: {
        cameraId: cameraId,
        name: cameraName,
        perspective: perspective,
        view: view,
      },
      uuid: camera.uuid,
      position: camera.position,
      rotation: camera.rotation,
      camera: camera,
    };
    this.activeCamera = renderData;
    this.cameraList.push(renderData);
  }

  @Watch('lastUpdateTime', { immediate: true })
  onDataChanged(): void {
    if (this.lastUpdateTime > -1 && this.activeCamera) {
      this.modelValue.updateDB(this.activeCamera.uuid);
    }
  }

  changeView(views: string | any[]): void {
    if (views.length === 0) return;
    this.cameraData.name = this.cameraData.view;
  }

  selectedObjectChanged(): void {
    for (const item of this.cameraList) {
      item.position = item.position.clone();
      item.rotation = item.rotation.clone();
    }
    const selection = this.selectionList.getSelectedObject();
    if (selection && (selection as any).isCamera) {
      const item = this.cameraList.find((camera) => camera.uuid === selection.uuid);
      if (item) {
        this.activeCamera = item;
        if (this.troisRenderer.camera) {
          this.troisRenderer.camera.position.copy(item.position);
          this.troisRenderer.camera.rotation.copy(item.rotation);
          item.position = this.troisRenderer.camera.position;
          item.rotation = this.troisRenderer.camera.rotation;
        }
      }
    }
  }
  //#endregion add / select camera

  //#region undo / redo
  undoRedoCamera(cameraId: string, position: THREE.Vector3, rotation: THREE.Euler): void {
    const cameraItem = this.cameraList.find((camera) => camera.uuid === cameraId);
    if (cameraItem) {
      this.selectObject(cameraItem.camera, true).then(() => {
        if (this.troisRenderer.camera) {
          cameraItem.position.copy(position.clone());
          cameraItem.rotation.copy(rotation.clone());
        }
      });
    }
  }
  //#endregion undo / redo

  //#region render view
  renderActiveView(): void {
    this.renderCamera(this.troisRenderer.camera);
  }

  async renderAllViews(): Promise<void> {
    for (const camera of this.cameraList) {
      camera.camera.position.copy(camera.position);
      camera.camera.rotation.copy(camera.rotation);
      if (camera.camera instanceof THREE.PerspectiveCamera) {
        const perspectiveCamera = camera.camera as THREE.PerspectiveCamera;
        perspectiveCamera.aspect = (this.troisRenderer.camera as THREE.PerspectiveCamera).aspect;
        perspectiveCamera.updateProjectionMatrix();
      }
      this.renderCamera(camera.camera);
    }
  }

  renderCamera(camera: THREE.Camera | undefined, returnBlobURL = false): Promise<string> {
    return new Promise((resolve) => {
      if (this.troisRenderer.scene && camera) {
        this.troisRenderer.renderer.clear();
        this.troisRenderer.renderer.render(this.troisRenderer.scene, camera);
      }

      this.troisRenderer.canvas.toBlob((blob) => {
        if (blob) {
          const blobURL = URL.createObjectURL(blob);
          if (!returnBlobURL) {
            window.open(blobURL, '_blank');
          }
          resolve(blobURL);
        } else {
          resolve('');
        }
      });
    });
  }
  //#endregion render view

  //#region import / export
  exportCameras(): CameraExportData[] {
    return this.cameraList.map((data) => {
      return {
        cameraId: data.info.cameraId,
        name: data.info.name,
        view: data.info.view,
        perspective: data.info.perspective,
        position: data.position.clone(),
        rotation: data.rotation.clone(),
      };
    });
  }

  async importCameras(data: CameraExportData[]): Promise<void> {
    if (this.troisRenderer.camera) {
      for (const item of data) {
        this.saveCamera(item.cameraId, item.name, item.perspective, item.view, item.position, item.rotation, false);
      }
    }
    this.onCanRenderChanged();
  }
  //#endregion import / export

  async exportCameraCaptureToPDF(blobURLs: BlobURL[]): Promise<void> {
    // Craete PDF template
    const content = createPDFRenderObjectView(blobURLs);
    // Generate PDF
    generatePDF(content, 'camera-capture');
  }

  async multipleCameraCapture(node: Node): Promise<void> {
    let blobURLs: BlobURL[] = [];
    this.isLoadingCapture = true;

    try {
      const threeData: MeshTreeDataItem[] = this.modelValue.getActiveTreeData();
      const threeDataChildren = threeData[1]?.children;

      if (threeData.length > 0 && threeDataChildren.length > 0) {
        node.expanded = true;
        const activeTreeData = threeDataChildren;

        for (const item of activeTreeData) {
          const uuid = item.uuid;
          const name = item.name;

          await this.modelValue.selectItem(item, true, true);
          await new Promise((resolve) => setTimeout(resolve, 3000));

          // Get object 3D image url
          const blobUrl = await this.renderCamera(this.troisRenderer.camera, true);
          blobURLs = [...blobURLs, { uuid, name, blobUrl }];
        }
      }
    } catch (error) {
      throw new Error(`Error: ${error}`);
    } finally {
      await this.exportCameraCaptureToPDF(blobURLs);
      this.isLoadingCapture = false;
    }
  }

  @Watch('modelValue.lastUpdateStructure', { immediate: false, deep: false })
  onListCameraChanged(): void {
    const threeData: MeshTreeDataItem[] = this.modelValue.getActiveTreeData();
    this.itemCameraCount = threeData[1]?.children?.length || 0;
  }
}
</script>

<style lang="scss" scoped>
.el-button {
  margin-left: 0;
  margin-top: 0.3rem;
}

.active {
  color: var(--el-color-primary);
}

.level-item:not(:last-child) {
  display: flex;
  align-items: center;
  margin-right: 0.6rem;
  margin-bottom: 0;
}
</style>
